"use client";

import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type ClipboardEvent,
  type KeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type SyntheticEvent,
} from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { getFontStack, type PdfFontKey } from "@/src/lib/pdf/fonts";
import type { DocumentDirection } from "@/src/lib/pdf/document-direction";
import {
  serializeScriptFallbacks,
  type ScriptFallbackKey,
} from "@/src/lib/pdf/script-fallbacks";
import { classNames } from "../ui/class-names";
import { paneVisibility } from "../layout/pane-visibility";
import {
  buildVisibleMarkdown,
  getCollapsedFoldClipboardText,
  getHiddenLineIndexes,
  getLineStartOffset,
  getVisibleFullLineIndexes,
  isAtxHeadingLine,
  mergeVisibleMarkdown,
  pruneCollapsedHeadingKeys,
  scanEditorHeadings,
  type EditorHeading,
} from "./heading-fold";
import {
  applyEditorCommand,
  getFormattingShortcutCommand,
  getHighlightedVisibleLineRange,
  getTextareaCursorLine,
  getTextareaSelection,
  indentSelection,
  isUndoRedoShortcut,
  normalizeSelection,
  outdentSelection,
  sliceLineSelection,
} from "./markdown-commands";
import type {
  EditorChange,
  EditorCommand,
  EditorSelection,
  WorkspaceTab,
} from "./types";

export type EditorPaneHandle = {
  runCommand: (command: EditorCommand) => void;
  undo: () => void;
  redo: () => void;
};

export const EditorPane = forwardRef<
  EditorPaneHandle,
  Readonly<{
    activeTab: WorkspaceTab;
    fontFamily: PdfFontKey;
    fontSize: number;
    markdown: string;
    scriptFallbacks?: readonly ScriptFallbackKey[];
    /** Document writing direction (Arabic-majority markdown → rtl). */
    dir?: DocumentDirection;
    lang?: string;
    onMarkdownChange: (markdown: string) => void;
    onRedo: () => EditorSelection | null;
    onUndo: () => EditorSelection | null;
    scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  }>
>(function EditorPane(
  {
    activeTab,
    fontFamily,
    fontSize,
    markdown,
    scriptFallbacks = [],
    dir = "ltr",
    lang = "en",
    onMarkdownChange,
    onRedo,
    onUndo,
    scrollContainerRef,
  },
  ref,
) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const gutterDragRef = useRef<{
    pointerId: number;
    startY: number;
    startScrollTop: number;
  } | null>(null);
  const [isGutterDragging, setIsGutterDragging] = useState(false);
  const [pendingSelection, setPendingSelection] =
    useState<EditorSelection | null>(null);
  const [collapsedHeadingKeys, setCollapsedHeadingKeys] = useState<Set<string>>(
    () => new Set(),
  );
  const lines = useMemo(() => markdown.split("\n"), [markdown]);
  const headings = useMemo(() => scanEditorHeadings(lines), [lines]);
  const headingsByFullIndex = useMemo(() => {
    const map = new Map<number, EditorHeading>();
    for (const heading of headings) {
      map.set(heading.index, heading);
    }
    return map;
  }, [headings]);

  const activeCollapsedKeys = useMemo(
    () => pruneCollapsedHeadingKeys(headings, collapsedHeadingKeys),
    [headings, collapsedHeadingKeys],
  );
  const hiddenLineIndexes = useMemo(
    () => getHiddenLineIndexes(headings, activeCollapsedKeys),
    [headings, activeCollapsedKeys],
  );
  const visibleFullLineIndexes = useMemo(
    () => getVisibleFullLineIndexes(lines.length, hiddenLineIndexes),
    [lines.length, hiddenLineIndexes],
  );
  const visibleLines = useMemo(
    () => visibleFullLineIndexes.map((index) => lines[index] ?? ""),
    [lines, visibleFullLineIndexes],
  );
  const visibleMarkdown = useMemo(
    () => buildVisibleMarkdown(lines, hiddenLineIndexes),
    [lines, hiddenLineIndexes],
  );

  // Drop stale fold keys after edits remove or empty a heading section.
  useEffect(() => {
    setCollapsedHeadingKeys((previous) => {
      const next = pruneCollapsedHeadingKeys(headings, previous);
      if (
        next.size === previous.size &&
        [...next].every((key) => previous.has(key))
      ) {
        return previous;
      }
      return next;
    });
  }, [headings]);

  // 1-based inclusive visible-line range for lite-orange / gutter chrome
  // (caret → one line; multi-line selection → every selected line).
  const [highlightedVisibleLines, setHighlightedVisibleLines] = useState<{
    start: number;
    end: number;
  } | null>(null);
  // Character range for deep-orange overlay selection (native ::selection is hidden).
  const [overlaySelection, setOverlaySelection] =
    useState<EditorSelection | null>(null);
  // Wrapped pixel height of each visible logical line.
  const [lineHeights, setLineHeights] = useState<number[] | null>(null);
  const [layoutVersion, setLayoutVersion] = useState(0);
  const overlayRef = useRef<HTMLDivElement>(null);

  const visibleLineStarts = useMemo(() => {
    const starts: number[] = [];
    let offset = 0;
    for (const line of visibleLines) {
      starts.push(offset);
      offset += line.length + 1;
    }
    return starts;
  }, [visibleLines]);

  const highlightedFullLineNumbers = useMemo(() => {
    if (highlightedVisibleLines === null) {
      return null;
    }

    const numbers = new Set<number>();
    for (
      let visibleLine = highlightedVisibleLines.start;
      visibleLine <= highlightedVisibleLines.end;
      visibleLine += 1
    ) {
      const fullIndex = visibleFullLineIndexes[visibleLine - 1];
      if (fullIndex !== undefined) {
        numbers.add(fullIndex + 1);
      }
    }
    return numbers;
  }, [highlightedVisibleLines, visibleFullLineIndexes]);

  useLayoutEffect(() => {
    if (!pendingSelection) {
      return;
    }

    const textarea = textareaRef.current;

    if (!textarea) {
      return;
    }

    // Clamp into the visible textarea (undo/redo may target full-doc offsets).
    const maxOffset = textarea.value.length;
    const selection = normalizeSelection({
      start: Math.min(pendingSelection.start, maxOffset),
      end: Math.min(pendingSelection.end, maxOffset),
    });
    const scrollContainer = scrollContainerRef.current;
    const scrollTop = scrollContainer?.scrollTop ?? 0;

    // Toolbar clicks move focus off the textarea. Restoring focus without
    // preventScroll makes the browser scroll the tall content-sized textarea
    // into view, which jumps the outer editor scroller to the document end.
    textarea.focus({ preventScroll: true });
    textarea.setSelectionRange(selection.start, selection.end);

    if (scrollContainer) {
      scrollContainer.scrollTop = scrollTop;
    }

    setOverlaySelection(
      selection.start === selection.end ? null : selection,
    );
    setHighlightedVisibleLines(
      getHighlightedVisibleLineRange(textarea.value, selection),
    );
    setPendingSelection(null);
  }, [visibleMarkdown, pendingSelection, scrollContainerRef]);

  useEffect(() => {
    const updateHighlightChrome = () => {
      const textarea = textareaRef.current;
      if (!textarea || document.activeElement !== textarea) {
        return;
      }
      const selection = getTextareaSelection(textarea);
      setOverlaySelection(
        selection.start === selection.end ? null : normalizeSelection(selection),
      );
      setHighlightedVisibleLines(
        getHighlightedVisibleLineRange(textarea.value, selection),
      );
    };

    // Prefer the textarea target: document-level selectionchange is unreliable
    // for <textarea> in Firefox. Keep document as a fallback for other engines.
    const textarea = textareaRef.current;
    textarea?.addEventListener("selectionchange", updateHighlightChrome);
    document.addEventListener("selectionchange", updateHighlightChrome);
    return () => {
      textarea?.removeEventListener("selectionchange", updateHighlightChrome);
      document.removeEventListener("selectionchange", updateHighlightChrome);
    };
  }, []);

  useLayoutEffect(() => {
    const textarea = textareaRef.current;

    if (!textarea) {
      return;
    }

    let lastWidth = textarea.clientWidth;
    const bump = () => setLayoutVersion((value) => value + 1);
    const onResize = (entries: ResizeObserverEntry[]) => {
      const width = entries[0]?.contentRect.width;
      if (width !== undefined && Math.abs(width - lastWidth) > 0.5) {
        lastWidth = width;
        bump();
      }
    };

    const ResizeObserverConstructor = globalThis.ResizeObserver;

    if (!ResizeObserverConstructor) {
      window.addEventListener("resize", bump);
      document.fonts?.ready.then(bump).catch(() => undefined);
      return () => {
        window.removeEventListener("resize", bump);
      };
    }

    const observer = new ResizeObserverConstructor(onResize);
    observer.observe(textarea);
    window.addEventListener("resize", bump);
    document.fonts?.ready.then(bump).catch(() => undefined);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", bump);
    };
  }, []);

  const scriptFallbacksKey = serializeScriptFallbacks(scriptFallbacks);

  useEffect(() => {
    const bump = () => setLayoutVersion((value) => value + 1);
    const fontFace = `1em ${getFontStack(fontFamily, scriptFallbacks)}`;

    bump();

    if (typeof document !== "undefined" && document.fonts) {
      document.fonts.load(fontFace).then(bump, () => bump());
    }
  }, [fontFamily, fontSize, scriptFallbacks, scriptFallbacksKey]);

  useLayoutEffect(() => {
    const overlay = overlayRef.current;

    if (!overlay) {
      return;
    }

    const blocks = Array.from(overlay.children) as HTMLDivElement[];
    const nextHeights = blocks.map((block) => block.getBoundingClientRect().height);

    setLineHeights((previous) => {
      if (
        previous !== null &&
        previous.length === nextHeights.length &&
        previous.every((value, index) => Math.abs(value - nextHeights[index]) < 0.01)
      ) {
        return previous;
      }
      return nextHeights;
    });
  }, [visibleMarkdown, layoutVersion]);

  function commitFullMarkdown(
    nextFullMarkdown: string,
    selection: EditorSelection | null = null,
  ) {
    if (selection) {
      setPendingSelection(selection);
    }
    onMarkdownChange(nextFullMarkdown);
  }

  function commitVisibleChange(change: EditorChange) {
    const nextFull = mergeVisibleMarkdown(
      lines,
      hiddenLineIndexes,
      change.value,
    );
    commitFullMarkdown(nextFull, change.selection);
  }

  function syncHighlightChrome(textarea: HTMLTextAreaElement) {
    const selection = getTextareaSelection(textarea);
    setOverlaySelection(
      selection.start === selection.end ? null : normalizeSelection(selection),
    );
    setHighlightedVisibleLines(
      getHighlightedVisibleLineRange(textarea.value, selection),
    );
  }

  /** Mouseup can run before the browser commits caret collapse inside an
   *  existing selection — defer so we read the settled range. */
  function scheduleHighlightChromeSync(textarea: HTMLTextAreaElement) {
    requestAnimationFrame(() => {
      if (textareaRef.current !== textarea) {
        return;
      }
      if (document.activeElement !== textarea) {
        return;
      }
      syncHighlightChrome(textarea);
    });
  }

  function handleEditorChange(event: ChangeEvent<HTMLTextAreaElement>) {
    syncHighlightChrome(event.currentTarget);
    const nextFull = mergeVisibleMarkdown(
      lines,
      hiddenLineIndexes,
      event.currentTarget.value,
    );
    onMarkdownChange(nextFull);
  }

  function handleEditorCopyOrCut(event: ClipboardEvent<HTMLTextAreaElement>) {
    const selection = getTextareaSelection(event.currentTarget);
    const expanded = getCollapsedFoldClipboardText(
      lines,
      hiddenLineIndexes,
      visibleMarkdown,
      selection,
    );

    if (expanded === null) {
      return;
    }

    event.preventDefault();
    event.clipboardData.setData("text/plain", expanded);

    if (event.type !== "cut") {
      return;
    }

    const { start, end } = normalizeSelection(selection);
    const nextVisible =
      visibleMarkdown.slice(0, start) + visibleMarkdown.slice(end);
    commitVisibleChange({
      value: nextVisible,
      selection: { start, end: start },
    });
  }

  function handleEditorCursorEvent(event: SyntheticEvent<HTMLTextAreaElement>) {
    scheduleHighlightChromeSync(event.currentTarget);
  }

  function handleCommand(command: EditorCommand) {
    const textarea = textareaRef.current;
    const selection = textarea
      ? getTextareaSelection(textarea)
      : { start: visibleMarkdown.length, end: visibleMarkdown.length };

    commitVisibleChange(
      applyEditorCommand(command, visibleMarkdown, selection),
    );
  }

  function handleUndo() {
    const selection = onUndo();

    if (selection) {
      // Full-doc offsets from history are clamped to the visible textarea.
      setPendingSelection(selection);
    }
  }

  function handleRedo() {
    const selection = onRedo();

    if (selection) {
      setPendingSelection(selection);
    }
  }

  useImperativeHandle(ref, () => ({
    runCommand: handleCommand,
    undo: handleUndo,
    redo: handleRedo,
  }));

  function handleEditorKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (isUndoRedoShortcut(event)) {
      event.preventDefault();

      if (event.shiftKey) {
        handleRedo();
      } else {
        handleUndo();
      }

      return;
    }

    const formattingCommand = getFormattingShortcutCommand(event);

    if (formattingCommand) {
      event.preventDefault();
      handleCommand(formattingCommand);
      return;
    }

    if (event.key !== "Tab") {
      return;
    }

    event.preventDefault();

    const selection = getTextareaSelection(event.currentTarget);
    const change = event.shiftKey
      ? outdentSelection(visibleMarkdown, selection)
      : indentSelection(visibleMarkdown, selection);

    commitVisibleChange(change);
  }

  function handleGutterPointerDown(event: ReactPointerEvent<HTMLDivElement>) {
    if (event.button !== 0) {
      return;
    }

    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) {
      return;
    }

    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    gutterDragRef.current = {
      pointerId: event.pointerId,
      startY: event.clientY,
      startScrollTop: scrollContainer.scrollTop,
    };
    setIsGutterDragging(true);
  }

  function handleGutterPointerMove(event: ReactPointerEvent<HTMLDivElement>) {
    const drag = gutterDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) {
      return;
    }

    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) {
      return;
    }

    const deltaY = event.clientY - drag.startY;
    scrollContainer.scrollTop = drag.startScrollTop - deltaY;
  }

  function endGutterDrag(event: ReactPointerEvent<HTMLDivElement>) {
    const drag = gutterDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) {
      return;
    }

    gutterDragRef.current = null;
    setIsGutterDragging(false);

    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture?.(event.pointerId);
    }
  }

  function handleToggleFold(heading: EditorHeading) {
    if (!heading.foldable) {
      return;
    }

    const willCollapse = !activeCollapsedKeys.has(heading.key);

    if (willCollapse) {
      const textarea = textareaRef.current;
      if (textarea) {
        const visibleCursorLine = getTextareaCursorLine(textarea);
        const cursorFullIndex =
          visibleFullLineIndexes[visibleCursorLine - 1] ?? null;

        if (
          cursorFullIndex !== null &&
          cursorFullIndex >= heading.foldRange.start &&
          cursorFullIndex < heading.foldRange.end
        ) {
          const visibleHeadingIndex = visibleFullLineIndexes.indexOf(
            heading.index,
          );
          if (visibleHeadingIndex >= 0) {
            const offset = getLineStartOffset(visibleLines, visibleHeadingIndex);
            setPendingSelection({ start: offset, end: offset });
          }
        }
      }
    }

    setCollapsedHeadingKeys((previous) => {
      const next = new Set(previous);
      if (next.has(heading.key)) {
        next.delete(heading.key);
      } else {
        next.add(heading.key);
      }
      return next;
    });

    const textarea = textareaRef.current;
    if (textarea && document.activeElement !== textarea) {
      textarea.focus({ preventScroll: true });
    }
  }

  return (
    <section
      id="editor-pane"
      role="tabpanel"
      aria-labelledby="editor-tab"
      className={classNames(
        paneVisibility("editor", activeTab),
        "relative min-h-0 min-w-0 flex-col editor-surface",
      )}
    >
      <div
        ref={scrollContainerRef}
        data-testid="editor-scroll-container"
        className="scrollbar-hidden editor-gutter-width relative grid min-h-0 flex-1 grid-cols-[var(--gutter-width)_minmax(0,1fr)] overflow-auto editor-surface"
        style={{
          "--line-number-digits": Math.max(String(lines.length).length, 2),
          fontFamily: getFontStack(fontFamily, scriptFallbacks),
          fontSize: `${fontSize}pt`,
        } as React.CSSProperties}
      >
        <div
          data-testid="editor-gutter"
          data-dragging={isGutterDragging ? "true" : undefined}
          className="relative z-10 border-r editor-gutter py-5 tabular-nums"
          style={{
            fontFamily: getFontStack(fontFamily, scriptFallbacks),
            fontSize: `${fontSize}pt`,
            lineHeight: 1.6,
          }}
          onPointerDown={handleGutterPointerDown}
          onPointerMove={handleGutterPointerMove}
          onPointerUp={endGutterDrag}
          onPointerCancel={endGutterDrag}
          onLostPointerCapture={() => {
            gutterDragRef.current = null;
            setIsGutterDragging(false);
          }}
        >
          {visibleFullLineIndexes.map((fullIndex, visibleIndex) => {
            const number = fullIndex + 1;
            const heading = headingsByFullIndex.get(fullIndex);
            const isCollapsed = heading
              ? activeCollapsedKeys.has(heading.key)
              : false;
            const showFold = Boolean(heading?.foldable);
            const isHighlighted =
              highlightedFullLineNumbers?.has(number) ?? false;
            const rowClassName = classNames(
              "editor-gutter-row",
              isHighlighted
                ? "editor-active-line-number border-l-[3px]"
                : "border-l-[3px] border-transparent",
            );
            const rowStyle =
              lineHeights && lineHeights[visibleIndex]
                ? { height: `${lineHeights[visibleIndex]}px` }
                : undefined;

            if (showFold && heading) {
              return (
                <button
                  key={number}
                  type="button"
                  style={rowStyle}
                  className={classNames(rowClassName, "editor-fold-button")}
                  aria-label={
                    isCollapsed
                      ? `Expand section ${heading.title || heading.key}`
                      : `Collapse section ${heading.title || heading.key}`
                  }
                  aria-expanded={!isCollapsed}
                  onPointerDown={(event) => {
                    // Fold click must not start gutter drag-to-scroll.
                    event.stopPropagation();
                  }}
                  onMouseDown={(event) => {
                    // Keep textarea selection; avoid focus steal before click.
                    event.preventDefault();
                  }}
                  onClick={() => handleToggleFold(heading)}
                >
                  <span
                    className={classNames(
                      "editor-gutter-number",
                      isHighlighted ? "editor-active-line-number" : undefined,
                    )}
                  >
                    {number}
                  </span>
                  {isCollapsed ? (
                    <ChevronRight aria-hidden="true" className="editor-fold-icon" />
                  ) : (
                    <ChevronDown aria-hidden="true" className="editor-fold-icon" />
                  )}
                </button>
              );
            }

            return (
              <div key={number} style={rowStyle} className={rowClassName}>
                <span
                  className={classNames(
                    "editor-gutter-number",
                    isHighlighted ? "editor-active-line-number" : undefined,
                  )}
                >
                  {number}
                </span>
                <span className="editor-fold-spacer" aria-hidden="true" />
              </div>
            );
          })}
        </div>
        <div className="relative min-w-0">
          {/* Layout mirror: shares the textarea's box, font, padding, and
              wrapping so each block's height equals the textarea's wrapped line
              height. Also paints visible text (textarea is transparent) so
              headings can be emphasized without affecting PDF. */}
          <div
            ref={overlayRef}
            aria-hidden="true"
            dir={dir}
            lang={lang}
            className="pointer-events-none absolute inset-0 z-0 select-none whitespace-pre-wrap break-words px-4 py-5 text-black sm:px-5"
            style={{
              fontFamily: getFontStack(fontFamily, scriptFallbacks),
              fontSize: `${fontSize}pt`,
              lineHeight: 1.6,
            }}
          >
            {visibleLines.map((line, index) => {
              const visibleLineNumber = index + 1;
              const isHighlighted =
                highlightedVisibleLines !== null &&
                visibleLineNumber >= highlightedVisibleLines.start &&
                visibleLineNumber <= highlightedVisibleLines.end;
              const lineStart = visibleLineStarts[index] ?? 0;
              const selectionParts = overlaySelection
                ? sliceLineSelection(line, lineStart, overlaySelection)
                : null;

              return (
                <div
                  key={visibleFullLineIndexes[index]}
                  className={classNames(
                    // Bleed into the overlay's horizontal padding so the active
                    // line highlight spans gutter→pane edge with no white gaps.
                    "-mx-4 px-4 sm:-mx-5 sm:px-5",
                    isHighlighted ? "editor-active-line" : undefined,
                    // Editor-only heading rule (overlay chrome; not in MD/PDF).
                    isAtxHeadingLine(line) ? "editor-heading-line" : undefined,
                  )}
                  style={{ minHeight: "1.6em" }}
                >
                  {selectionParts ? (
                    <>
                      {selectionParts.before}
                      <span className="editor-selection">
                        {selectionParts.selected}
                      </span>
                      {selectionParts.after}
                    </>
                  ) : (
                    line
                  )}
                </div>
              );
            })}
          </div>
          <textarea
            ref={textareaRef}
            id="markdown-editor"
            aria-label="Markdown editor"
            dir={dir}
            lang={lang}
            spellCheck={false}
            value={visibleMarkdown}
            onChange={handleEditorChange}
            onCopy={handleEditorCopyOrCut}
            onCut={handleEditorCopyOrCut}
            onKeyDown={handleEditorKeyDown}
            onKeyUp={handleEditorCursorEvent}
            onMouseUp={handleEditorCursorEvent}
            onSelect={handleEditorCursorEvent}
            onFocus={handleEditorCursorEvent}
            onBlur={() => {
              setHighlightedVisibleLines(null);
              setOverlaySelection(null);
            }}
            className="editor-textarea relative z-10 min-h-full w-full min-w-0 resize-none overflow-hidden whitespace-pre-wrap break-words border-0 bg-transparent px-4 py-5 text-transparent caret-black outline-none sm:px-5"
            style={{
              fontFamily: getFontStack(fontFamily, scriptFallbacks),
              fontSize: `${fontSize}pt`,
              lineHeight: 1.6,
            }}
          />
        </div>
      </div>
    </section>
  );
});
